import { useEffect, useState } from 'react';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../lib/auth';
import { clearIdleSignOutNotice, peekIdleSignOutNotice } from '../lib/idleSignOut';

export default function SignIn() {
  const { signInWithGoogle, signInWithApple, signInWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read during render and clear in an effect, rather than consuming in one step. Under
  // StrictMode both run twice, and a read that cleared would show the notice to whichever
  // pass happened to go first.
  const [idleSignOut] = useState(peekIdleSignOutNotice);
  useEffect(clearIdleSignOutNotice, []);

  // Every failure path ends in a visible message. The Android client shipped with empty
  // catch blocks here and a tester read the silence as the app hanging; not repeating it.
  async function attempt(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      setError(
        code === 'auth/popup-closed-by-user'
          ? null
          : `${(e as Error).message}${code ? ` (${code})` : ''}`
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="centre">
      <div className="card">
        <div className="brand brand-lg">
          <img src="/zsoogi-logo.png" alt="" width={40} height={40} />
          <h1>ZsoogiNotes</h1>
        </div>
        <p className="muted">Your ideas, captured on your phone — searchable here.</p>

        {idleSignOut && (
          <p className="notice">Signed out after an hour without activity.</p>
        )}

        <button className="primary" disabled={busy} onClick={() => attempt(signInWithGoogle)}>
          Sign in with Google
        </button>

        <button className="apple" disabled={busy} onClick={() => attempt(signInWithApple)}>
          {/* Apple require their own mark and colours on this button, which is why it is the
              one control here that ignores the palette. */}
          <svg viewBox="0 0 384 512" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
            />
          </svg>
          Sign in with Apple
        </button>

        <div className="divider"><span>or</span></div>

        <form
          className="signin-email"
          onSubmit={(e) => {
            e.preventDefault();
            void attempt(() => signInWithEmail(email, password));
          }}
        >
          <input
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit" disabled={busy}>Sign in</button>
        </form>

        {error && <p className="error">{error}</p>}

        {/* No sign-up on the web. Accounts are created in the phone apps, which is where
            capture happens — and it keeps this public surface from minting accounts. */}
        <p className="muted small">New here? Create your account in the iOS or Android app.</p>

        <ThemeToggle />
      </div>
    </main>
  );
}
